/**
 * @module app.cm
 */


import { autoinject } from 'aurelia-framework';
import { ComponentBase, IForm, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { apiCall } from 'app/shared/lib/essette-api';
import { CaseService } from 'app/cm/services/case-service';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { EssModalCustomElement } from 'app/shared/elements/ess-dialog';
import { SaveReferralDestinationCustomElement } from 'app/cm/admin/referral/save-referral-destination';
import { validate } from 'app/shared/elements/ess-rule';
import { Toaster } from 'app/shared/lib/toaster';
import { deepClone } from 'app/shared/lib/utilities';
import { confirmation } from 'app/shared/elements/ess-confirm';
import * as CM from 'app/cm/models/essette-api';


@autoinject()
export class ReferredTo extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected referralDestinations: ReadonlyArray<CM.ReferralDestination> = [];

    private sorting = new Sorting();
    protected resultCount = 0;
    protected pageSize = 25;
    protected includeInactive = false;
    protected isValid: boolean = false;
    protected validate = validate;

    ////****************** Element References ***************************************////
    protected $modal!: EssModalCustomElement;
    protected $form!: IForm;
    protected $saveReferralDestination!: SaveReferralDestinationCustomElement;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: CaseService,
        private readonly toaster: Toaster,
        private readonly caseService: CaseService,
        ...rest: any[]
    ) {
        super(...rest);

        // Default Sort
        this.sorting.entries = [
            {
                reference: 'description',
                direction: SortDirection.Ascending
            }
        ];
    }

    protected bind(): void {
        this.reload();
    }

    ////****************** Events ***************************************************////

    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
            if(this.sorting.entries.length == 0) {
                this.sorting.entries = [
                    {
                        reference: 'description',
                        direction: SortDirection.Ascending
                    }
                ];
            }
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }

        this.reload();
        //Call stop Propagation to prevent parent table from reload and losing sort
        event.stopPropagation();
    }

    private editReferralDestination(referralDestination: CM.ReferralDestination): void {
        if(!referralDestination || !this.$modal) {
            return;
        }

        this.$saveReferralDestination.referralDestination = deepClone(referralDestination);
        this.$modal.open();
    }

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;

    ////****************** Functions ************************************************////
    public async reload(): Promise<void> {
        await this.loadDestinationsForReferral();
    }

    protected addReferralDestination() {
        this.$modal.open();
    }

    protected modalClose(): void {
        if(this.$form.reset) {
            this.$form.reset();
        }
        this.$saveReferralDestination.reset();
        this.$modal.close();
    }

    protected async handleSaveReferralDestinationFormSuccess(): Promise<void> {
        if(this.$saveReferralDestination.reset) {
            this.$saveReferralDestination.reset();
        }
        this.$modal.close();
    }

    public async submit(): Promise<boolean> {
        const response = await this.$saveReferralDestination.saveReferralDestination();
        if(this.$form.reset) {
            this.$form.reset();
        }
        this.$modal.close();
        this.reload();
        return response;
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadDestinationsForReferral(): Promise<void> {
        const includes: string[] = [];
        const options: Partial<IQueryLoadOptions> = {
            sorting: this.sorting,
            top: this.pageSize,
            page: this.$pager?.page || 0,
            includeInactive: this.includeInactive
        };

        try {
            this.state.beginLoad();

            const response = await this.service.loadReferralDestinations(options, includes);
            this.referralDestinations = response.results;
            this.resultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async activateReferralDestination(referralDestination: CM.ReferralDestination, isActive: boolean) {
        if(!isActive) {
            const result = await confirmation.showDeactivate('cm:referral.referredTo.$');
            if(result.dialogResponse === confirmation.options.CANCEL) { return; }
        }

        try {
            this.state.beginSave();
            const response = await this.caseService.activateReferralDestination(referralDestination.id!, isActive);

            if(response.isSuccessful) {
                this.loadDestinationsForReferral();
            }
            this.toaster.standard.activate('cm:referral.referredTo.$', isActive, response.isSuccessful);

        } catch(error) {
            this.toaster.standard.activate('cm:referral.referredTo.$', isActive, false);
            throw error;
        } finally {
            this.state.endSave();
        }
    }
}
